/**
 * Окошко масштаба подложки: какой длины на самом деле отрезок, отмеченный на плане.
 *
 * В поле сразу стоит длина отрезка в текущем масштабе подложки — её правят на настоящую
 * (по рулетке или по цифре на плане БТИ). По «Применить» подложка растягивается так,
 * чтобы отрезок стал ровно этой длины.
 */
import { useForm } from "@tanstack/react-form";
import { z } from "zod";
import { dist } from "@/shared/lib/geometry";
import { UIKit } from "@/shared/ui";
import type { UnderlayMeasure } from "../hooks/useUnderlayTools";

const MIN_LENGTH = 100;
const MAX_LENGTH = 100_000;

const scaleSchema = z.object({
	length: z
		.number()
		.min(MIN_LENGTH, `Не меньше ${MIN_LENGTH} мм`)
		.max(MAX_LENGTH, `Не больше ${MAX_LENGTH / 1000} м`),
});

interface Props {
	measure: UnderlayMeasure;
	onApply: (length: number) => void;
	onClose: () => void;
}

export const UnderlayScaleDialog = ({ measure, onApply, onClose }: Props) => {
	const form = useForm({
		defaultValues: { length: Math.round(dist(measure.from, measure.to) / 10) * 10 },
		validators: { onChange: scaleSchema },
		onSubmit: ({ value }) => onApply(value.length),
	});

	return (
		<UIKit.ConfirmModal
			title="Масштаб плана"
			description="Какой длины на самом деле отмеченная стена? Возьмите цифру с плана БТИ или замерьте рулеткой — план растянется под неё."
			onClose={onClose}
		>
			<form className="mt-4 flex flex-col gap-3" action={() => form.handleSubmit()}>
				<UIKit.FormField form={form} name="length">
					{(field) => (
						<UIKit.NumberField
							label="Длина стены"
							value={field.state.value}
							min={MIN_LENGTH}
							max={MAX_LENGTH}
							onChange={field.handleChange}
						/>
					)}
				</UIKit.FormField>
				<div className="mt-2 flex justify-end gap-2">
					<UIKit.Button type="button" className="px-3 text-sm" onClick={onClose}>
						Отмена
					</UIKit.Button>
					<UIKit.Button
						type="submit"
						className="border-accent bg-accent px-3 text-sm font-medium text-white hover:border-blue-700 hover:bg-blue-700"
					>
						Применить
					</UIKit.Button>
				</div>
			</form>
		</UIKit.ConfirmModal>
	);
};
